import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import LoadingState from '../../charts/echarts/LoadingState'
import { useHideIfNoDataWhen } from '../HideIfNoData'
import useBlueprintData from './hooks/useBlueprintData'
import { LATCH_LATEST } from './constants'
import AxisChartLayout from './chartLayouts/AxisChartLayout'
import PieChartLayout from './chartLayouts/PieChartLayout'
import TreeMapLayout from './chartLayouts/TreeMapLayout'
import FunnelChartLayout from './chartLayouts/FunnelChartLayout'
import RingChartLayout from './chartLayouts/RingChartLayout'
import { useChartStyleVariant } from './hooks/useStyleVariant'

const LAYOUT_RENDERERS = {
  axis: AxisChartLayout,
  pie: PieChartLayout,
  treemap: TreeMapLayout,
  funnel: FunnelChartLayout,
  ring: RingChartLayout
}

const LAYOUT_LOADING_TEMPLATE = {
  axis: 'axis',
  pie: 'pie',
  treemap: 'pie',
  funnel: 'pie',
  ring: 'gauge'
}

/**
 * Fetches data for the primary purpose and resolves latch parameters.
 * Layout renderers may call useBlueprintData again for per-series overrides.
 */
function usePrimaryBlueprintData (blueprintCode, config, latch, parameters, scopePredicates) {
  const { purposeCode } = config
  const isConfigured = !!purposeCode

  const effectiveParameters = useMemo(() => {
    if (!latch) return parameters
    return { ...parameters, [latch.parameterName]: LATCH_LATEST }
  }, [parameters, latch])

  const { data, isLoading, error } = useBlueprintData(
    blueprintCode,
    isConfigured ? purposeCode : null,
    { parameters: effectiveParameters, pageSize: 1000, scopePredicates }
  )

  return { data, isLoading, error, effectiveParameters }
}

function BlueprintChart ({ blueprintCode, config, latch, parameters, scopePredicates }) {
  const { layout } = config

  const LayoutRenderer = LAYOUT_RENDERERS[layout]
  const variant = useChartStyleVariant(layout, config)

  const { data, isLoading, error, effectiveParameters } = usePrimaryBlueprintData(
    blueprintCode, config, latch, parameters, scopePredicates
  )
  useHideIfNoDataWhen(() => isLoading || !data?.length, [isLoading, data])

  if (!LayoutRenderer) {
    return (
      <div className='p-md font-size-sm' style={{ color: 'var(--color-warning-500)' }}>
        Unknown chart layout &quot;{layout}&quot;
      </div>
    )
  }

  if (!config.purposeCode) {
    return null
  }

  if (isLoading) {
    const loadingType = LAYOUT_LOADING_TEMPLATE[layout] ?? 'pie'
    const loadingHeight = config.height || 400
    const loadingWidth = layout === 'ring' ? config.width || 300 : undefined
    return (
      <div style={{ position: 'relative', height: loadingHeight, width: loadingWidth }}>
        <LoadingState type={loadingType} height={loadingHeight} />
      </div>
    )
  }

  if (error) {
    return (
      <div className='p-md font-size-sm' style={{ color: 'var(--color-danger-500)' }}>
        Chart error: {error.message ?? 'Unknown error'}
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: config.height || 400,
        color: 'var(--text-color-subtle)'
      }}
      >
        No data available
      </div>
    )
  }

  const content = (
    <LayoutRenderer
      blueprintCode={blueprintCode}
      config={config}
      data={data}
      parameters={effectiveParameters}
      latch={latch}
      styleOverrides={variant}
    />
  )

  if (variant.className) {
    return <div className={variant.className}>{content}</div>
  }

  return content
}

BlueprintChart.propTypes = {
  blueprintCode: PropTypes.string.isRequired,
  config: PropTypes.shape({
    layout: PropTypes.oneOf(['axis', 'pie', 'treemap', 'funnel', 'ring']).isRequired,
    purposeCode: PropTypes.string,
    height: PropTypes.number,
    width: PropTypes.number,
    colorTheme: PropTypes.string
  }).isRequired,
  latch: PropTypes.shape({
    parameterName: PropTypes.string.isRequired,
    dateColumn: PropTypes.string.isRequired,
    dateTable: PropTypes.string.isRequired,
    limit: PropTypes.number
  }),
  parameters: PropTypes.object,
  scopePredicates: PropTypes.arrayOf(PropTypes.object)
}

BlueprintChart.defaultProps = {
  parameters: {}
}

export default BlueprintChart
